import cpu from '../../../assets/svg-icons/blueCPU.svg';
import ram from '../../../assets/svg-icons/ram.svg';
import hdd from '../../../assets/svg-icons/disk.svg';
import { useState, useEffect, useContext, useRef } from 'react';
import { Backdrop, CircularProgress } from '@mui/material';
import closeIcon from '../../../assets/svg-icons/closeBrand.svg';
import Input from '../../../components/input';
import Select from '../../../components/select';
import ubuntu from '../../../assets/svg-icons/Ubuntu logo.svg';
import debian from '../../../assets/svg-icons/debian logo.svg';
import rocky from '../../../assets/svg-icons/rocky logo.svg';
import centOS from '../../../assets/svg-icons/centOS logo.svg';
import openSUSE from '../../../assets/svg-icons/openSUSE logo.svg';
import fedora from '../../../assets/svg-icons/fedora logo.svg';
import windows from '../../../assets/images/windows.png';
import oracle from '../../../assets/svg-icons/oracle-logo.svg';
import close from '../../../assets/svg-icons/closeBrand.svg';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  listImages,
  listKeypairs,
  createKeypair,
  createServer,
  getResourceStatus,
  listServers,
  listPrivateNetworks,
  getUserVirtualAccount,
} from '../../../hooks/local/userReducer';
import { apiEndPoints } from '../../../hooks/remote/apiEndPoints';
import { CartContext } from '../../../components/cartContext';
import {
  extractDiskSize,
  useListLocalImageDetails,
  useNetworkList,
  useServerList,
} from '../layout/resuableEffect';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Spinner from '../../../components/spinner';
import { toast } from 'react-toastify';
import { scroller } from 'react-scroll';
import { IoCopyOutline } from 'react-icons/io5';
import { SiAlmalinux } from 'react-icons/si';
import usePartnerNavigation from '../../../hooks/usePartnerNavigation';
import PaymentModal from '../../../components/paymentModal';

const ConfigureVPS = () => {

  const { navigate, generatePartnerUrl } = usePartnerNavigation();

  const dispatch = useDispatch();
  const location = useLocation();

  const isLoading = useSelector((state) => state.user.loading);
  const { images = [] } = useSelector((state) => state.user);
  const { keypairs = {} } = useSelector((state) => state.user);
  const { totalPrice, setTotalPrice, currency, duration, setDuration } =
    useContext(CartContext);
  const walletBalance = useSelector((state) =>
    state.user.virtualAccount
      ? currency === 'USD'
        ? state.user.virtualAccount.dollar_balance
        : state.user.virtualAccount.balance
      : 0,
  );
  const virtualAccount = useSelector((state) => state.user.virtualAccount);

  const [cartItems, setCartItems] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedValue, setSelectedValue] = useState('');
  const [selectedOption, setSelectedOption] = useState('existing');
  const [authMethod, setAuthMethod] = useState('keypairs');
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successDetails, setSuccessDetails] = useState({});
  const [extraVolumeSize, setExtraVolumeSize] = useState('');
  const [extraEgressSize, setExtraEgressSize] = useState('');
  const [networkType, setNetworkType] = useState('public');
  const [selectedPrivateNetwork, setSelectedPrivateNetwork] = useState('');
  const [privateNetworks, setPrivateNetworks] = useState([]);
  const [privateSubnets, setPrivateSubnets] = useState([]);
  const [subnetLoading, setSubnetLoading] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);

  const [showWalletModal, setShowWalletModal] = useState(false);


  const userServer = useServerList();
  const localImageDetails = useListLocalImageDetails();

  useEffect(() => {
    if (location.pathname === '/client-area/configure-vps') {
      const snapshot = sessionStorage.getItem('selectedServerSnapshot');
      if (snapshot) {
        const parsedSnapshot = JSON.parse(snapshot);
        const snapshotSize = parseFloat(
          parsedSnapshot.snapshotSize.replace(/ GB$/, ''),
        );
        const updatedSnapshot = { ...parsedSnapshot, snapshotSize };
        setSelectedSnapshot(updatedSnapshot);

        if (updatedSnapshot.action === 'launch') {
          setSelectedImage('snapshot');
          setSelectedImageRef(updatedSnapshot.snapshotId);
          setAuthMethod('keypairs');
          createNewServer.setFieldValue('imageRef', updatedSnapshot.snapshotId);
          createNewServer.setFieldValue('imagesRef', 'snapshot');

          const defaultUsername = getDefaultUsername(
            updatedSnapshot.snapshotOS,
          );
          createNewServer.setFieldValue('username', defaultUsername);
        }
      }
    }

    return () => {
      if (location.pathname !== '/client-area/configure-vps') {
        sessionStorage.removeItem('selectedServerSnapshot');
        setSelectedSnapshot(null);
      }
    };
  }, [location.pathname]);

  // Helper function to determine default username based on OS distro
  const getDefaultUsername = (osDistro) => {
    switch (osDistro?.toLowerCase()) {
      case 'ubuntu':
        return 'ubuntu';
      case 'centos':
        return 'cloud-user';
      case 'debian':
        return 'debian';
      case 'rocky':
        return 'rocky';
      case 'almalinux':
      case 'alma linux':
        return 'almalinux';
      case 'opensuse':
        return 'opensuse';
      case 'fedora':
        return 'fedora';
      case 'oracle':
      case 'oracle linux':
        return 'cloud-user';
      default:
        return '';
    }
  };

const normalizedUserServers = Array.isArray(userServer)
  ? userServer
  : Array.isArray(userServer?.servers)
    ? userServer.servers
    : Array.isArray(userServer?.data)
      ? userServer.data
      : Array.isArray(userServer?.results)
        ? userServer.results
        : [];

const userServerList = normalizedUserServers
  .filter((server) => server?.addresses)
  .map((server) => {
    const externalAddress = server?.addresses?.External?.[0]?.addr;

    const allAddresses = Object.values(server?.addresses || {})
      .flat()
      .map((address) => address?.addr)
      .filter(Boolean)
      .join(', ');

    return {
      serverId: server?.id,
      name: server?.name
        ? server.name.replace(/_/g, ' ')
        : '',
      address: externalAddress || allAddresses || '',
      status: server?.status === 'ACTIVE' ? 'Active' : 'Inactive',
    };
  });
  
  const errorRefs = {
    name: useRef(null),
    imagesRef: useRef(null),
    imageRef: useRef(null),
    old_key_pair_name: useRef(null),
    volume_size: useRef(null),
    free_egress: useRef(null),
    keypair_name: useRef(null),
    username: useRef(null),
    user_pass: useRef(null),
    confirm_pass: useRef(null),
    private_network: useRef(null),
    private_subnet: useRef(null),
  };

  const scrollContainerRef = useRef(null);

  const flavorRef = cartItems?.[0]?.productId;
  const flavorId = cartItems?.[0]?.flavorId;
  const storage = cartItems?.[0]?.diskSize;
  const freeEgress = cartItems?.[0]?.egressSize;

  const extraStorage = extractDiskSize(storage);

  const selectedProject = useSelector(
  (state) => state.user.selectedProject,
);

const selectedProjectId = selectedProject?.id;

const selectedZoneId =
  selectedProject?.zone_id ||
  selectedProject?.zone?.id ||
  selectedProject?.zone?.zone_id ||
  selectedProject?.zone_details?.id ||
  selectedProject?.zone_details?.zone_id;

  const [customFlavorPrice, setCustomFlavorPrice] = useState(null);
const [customFlavorPriceLoading, setCustomFlavorPriceLoading] =
  useState(false);

  const networkList = useNetworkList(selectedProjectId);

  useEffect(() => {
    if (networkType === 'private') {
      setPrivateNetworks(
        Array.isArray(networkList) ? networkList : [],
      );
    }
  }, [networkType, networkList]);

  useEffect(() => {
    const cart = sessionStorage.getItem('cart');
    if (cart) {
      const parsedCart = JSON.parse(cart);
      setCartItems(parsedCart);
      const initialPrice = parsedCart[0]?.price || 0;
      setTotalPrice(initialPrice);
      setDuration(parsedCart[0]?.paymentMethod || 'Monthly');
    } else {
      setCartItems([]);
      setTotalPrice(0);
    }
  }, [setTotalPrice, setDuration]);

const fetchCustomFlavorPrice = async () => {
  if (
    !cartItems?.length ||
    cartItems[0]?.type !== 'Customize' ||
    !selectedZoneId ||
    !duration
  ) {
    return;
  }

  const customFlavorDisc = parseInt(
    String(cartItems[0]?.diskSize || '').replace(/GB/gi, '').trim(),
    10,
  );

  const customFlavorRam = parseInt(
    String(cartItems[0]?.ramSize || '').replace(/GB/gi, '').trim(),
    10,
  );

  const customFlavorVcpu = parseInt(
    cartItems[0]?.vcpu,
    10,
  );

  const customFlavorPayload = {
    custom_flavor_ram: customFlavorRam,
    custom_flavor_disc: customFlavorDisc,
    custom_flavor_vpcu: customFlavorVcpu,
    payment_method: handlePaymentDuration(duration),
    zone_id: selectedZoneId,
  };

  console.log(
    'Custom Flavor Price Payload:',
    customFlavorPayload,
  );

  try {
    setCustomFlavorPriceLoading(true);

    const response =
      await apiEndPoints.getCustomFlavorPrice(
        customFlavorPayload,
      );

    const data =
      response?.data?.data ||
      response?.data ||
      response;

    console.log(
      'Custom Flavor Price Response:',
      data,
    );

    const price =
      currency === 'USD'
        ? data?.dollar_price ??
          data?.usd_price ??
          data?.price_usd ??
          data?.dollarPrice
        : data?.naira_price ??
          data?.ngn_price ??
          data?.price_ngn ??
          data?.nairaPrice ??
          data?.price;

    if (price !== undefined && price !== null) {
      const parsedPrice = Number(
        String(price)
          .replace(/NGN|USD|₦|\$/gi, '')
          .replace(/,/g, '')
          .trim(),
      );

      setCustomFlavorPrice(
        Number.isNaN(parsedPrice)
          ? null
          : parsedPrice,
      );
    } else {
      console.log(
        'Price field not found in response:',
        data,
      );

      setCustomFlavorPrice(null);
    }
  } catch (error) {
    console.error(
      'Custom flavor price error:',
      error,
    );

    setCustomFlavorPrice(null);

    toast.error(
      error?.response?.data?.statusmessage ||
        error?.response?.data?.message ||
        error?.message ||
        'Unable to calculate custom flavor price.',
    );
  } finally {
    setCustomFlavorPriceLoading(false);
  }
};

useEffect(() => {
  if (
    cartItems?.length > 0 &&
    cartItems[0]?.type === 'Customize' &&
    selectedZoneId &&
    duration
  ) {
    fetchCustomFlavorPrice();
  }
}, [
  cartItems,
  selectedZoneId,
  duration,
  currency,
]);

  const calculateExtraVolumeCost = (size, currency) => {
    const pricePerGB = currency === 'USD' ? 0.2 : 300;
    return size ? parseFloat(size) * pricePerGB : 0;
  };

  const calculateExtraEgressCost = (size, currency) => {
    // Get egress pricing from cart item if available, otherwise use default pricing
    const egressPricePerGB = cartItems[0]?.egressPrice
      ? parseFloat(cartItems[0].egressPrice.replace(/NGN|USD|\$|,/g, ''))
      : (currency === 'USD' ? 0.4 : 500);
    return size ? parseFloat(size) * egressPricePerGB : 0;
  };

const calculateTotalPrice = (
  cartItems,
  duration,
  extraVolumeSize,
  extraEgressSize,
) => {
  let totalCartPrice = 0;

  cartItems.forEach((item) => {
    // CUSTOM FLAVOR
    // Price now comes from the backend API
    if (
      item?.type === 'Customize' &&
      customFlavorPrice !== null
    ) {
      totalCartPrice += customFlavorPrice;
      return;
    }

    // NORMAL FLAVOR
    let priceString = '';

    if (currency === 'USD') {
      priceString = item.dollarPrice;
    } else if (currency === 'NGN') {
      priceString = item.nairaPrice;
    }

    const price = parseFloat(
      priceString
        ? String(priceString)
            .replace(/NGN|USD|₦|\$/gi, '')
            .replace(/,/g, '')
            .trim()
        : item.price || 0,
    );

    totalCartPrice +=
      Number.isNaN(price) ? 0 : price;
  });

  /*
   * IMPORTANT:
   * Don't multiply custom flavor by 12 here.
   *
   * The backend already received:
   * payment_method: monthly/yearly
   *
   * Therefore the API should return the appropriate
   * custom flavor price for that payment method.
   */
  const hasCustomFlavor = cartItems.some(
    (item) => item?.type === 'Customize',
  );

  if (
    duration === 'Yearly' &&
    !hasCustomFlavor
  ) {
    const yearlyPrice =
      totalCartPrice * 12;

    const discount =
      yearlyPrice * 0.02;

    totalCartPrice =
      yearlyPrice - discount;
  }

  const extraVolumeCost =
    calculateExtraVolumeCost(
      extraVolumeSize,
      currency,
    );

  const extraEgressCost =
    calculateExtraEgressCost(
      extraEgressSize,
      currency,
    );

  return (
    totalCartPrice +
    extraVolumeCost +
    extraEgressCost
  );
};

useEffect(() => {
  const newTotalPrice =
    calculateTotalPrice(
      cartItems,
      duration,
      extraVolumeSize,
      extraEgressSize,
    );

  setTotalPrice(newTotalPrice);
}, [
  cartItems,
  duration,
  extraVolumeSize,
  extraEgressSize,
  currency,
  customFlavorPrice, // IMPORTANT
  setTotalPrice,
]);

  const removeFromCart = () => {
    sessionStorage.removeItem('cart');
    setCartItems([]);
    setTotalPrice(0);
    window.location.reload();
  };

  useEffect(() => {
    dispatch(listImages());
  }, [dispatch]);

  useEffect(() => {
    dispatch(listKeypairs(selectedProjectId));
  }, [dispatch, selectedProjectId]);

  const handleSelect = (duration) => {
    setDuration(duration);
  };

  useEffect(() => {
    if (cartItems.length > 0 && cartItems[0].paymentMethod) {
      handleSelect(cartItems[0].paymentMethod);
    }
  }, [cartItems]);

  const activeStyle =
    'text-sm bg-primaryBlue/10 rounded px-5 md:px-10 h-14 flex items-center border border-primaryBlue cursor-pointer';
  const inactiveStyle =
    'text-sm bg-[#c4c4c416] rounded px-5 md:px-10 h-14 flex items-center border dark:border-[#c4c4c424] cursor-pointer';

  const formatOptions = (data = []) => {
    return data.map((item) => ({
      value: item?.image_id || item?.id,
      label: item?.name || '',
    }));
  };

  const formatPrivateNetworkOptions = (networks = []) => {
    return networks.map((network) => ({
      value: network.id,
      label: network.name,
    }));
  };

  const formatPrivateSubnetOptions = (subnets = []) => {
    return subnets.map((subnet) => ({
      value: subnet.id,
      label: subnet.name || subnet.id,
    }));
  };

  const formatKeypairOptions = (keypairs = []) => {
    return Array.isArray(keypairs)
      ? keypairs.map(({ keypair }) => ({
        value: keypair?.name,
        label: keypair?.name,
      }))
      : [];
  };

  const keypairOptions = Array.isArray(keypairs)
    ? formatKeypairOptions(keypairs)
    : [];

  const privateNetworkOptions = formatPrivateNetworkOptions(privateNetworks);
  const privateSubnetOptions = formatPrivateSubnetOptions(privateSubnets);

  const getAllImages = () => {
    // Current API response is a flat images array.
    if (Array.isArray(images)) {
      return images.filter(Boolean);
    }

    // Defensive fallback in case a wrapped/grouped response is ever stored.
    if (Array.isArray(images?.images)) {
      return images.images.filter(Boolean);
    }

    if (images && typeof images === 'object') {
      return Object.values(images)
        .flatMap((group) => (Array.isArray(group) ? group : []))
        .filter(Boolean);
    }

    return [];
  };

  const allAvailableImages = getAllImages();

  const filterImagesByOS = (...keywords) => {
    const normalizedKeywords = keywords.map((keyword) =>
      String(keyword).toLowerCase(),
    );

    return allAvailableImages.filter((item) => {
      const name = String(item?.name || '').toLowerCase();
      const username = String(item?.username || '').toLowerCase();
      const distro = String(item?.os_distro || '').toLowerCase();

      return normalizedKeywords.some(
        (keyword) =>
          name.includes(keyword) ||
          username.includes(keyword) ||
          distro.includes(keyword),
      );
    });
  };

  const ubuntuOptions = formatOptions(
    filterImagesByOS('ubuntu'),
  );

  const debianOptions = formatOptions(
    filterImagesByOS('debian'),
  );

  const rockyOptions = formatOptions(
    filterImagesByOS('rocky'),
  );

  const almalinuxOptions = formatOptions(
    filterImagesByOS('almalinux', 'alma linux'),
  );

  const centosOptions = formatOptions(
    filterImagesByOS('centos'),
  );

  const opensuseOptions = formatOptions(
    filterImagesByOS('opensuse', 'open suse', 'suse'),
  );

  const fedoraOptions = formatOptions(
    filterImagesByOS('fedora', 'fedora-coreos'),
  );

  const windowsOptions = formatOptions(
    filterImagesByOS('windows', 'windows server'),
  );

  const oracleOptions = formatOptions(
    filterImagesByOS('oracle', 'oracle linux'),
  );

  const allUbuntuOptions = ubuntuOptions;
  const allCentosOptions = centosOptions;

  const getOptionsForSelectedImage = (selectedImage) => {
    switch (selectedImage) {
      case 'ubuntu':
        return allUbuntuOptions;
      case 'debian':
        return debianOptions;
      case 'rocky':
        return rockyOptions;
      case 'almalinux':
        return almalinuxOptions;
      case 'centos':
        return allCentosOptions;
      case 'opensuse':
        return opensuseOptions;
      case 'fedora':
        return fedoraOptions;
      case 'windows':
        return windowsOptions;
      case 'oracle':
        return oracleOptions;
      case 'snapshot':
        return location.pathname === '/client-area/configure-vps' &&
          selectedSnapshot &&
          selectedSnapshot.action === 'launch'
          ? [
            {
              value: selectedSnapshot.snapshotId,
              label: selectedSnapshot.snapshotName,
            },
          ]
          : [];
      default:
        return [];
    }
  };

  const handleImageClick = (image) => {
    if (
      location.pathname === '/client-area/configure-vps' &&
      selectedSnapshot &&
      selectedSnapshot.action === 'launch'
    )
      return;
    setSelectedImage(image);
    setSelectedValue('');
    setAuthMethod(image === 'windows' ? 'password' : 'keypairs');

    const options = getOptionsForSelectedImage(image);
    const defaultImageRef = options.length > 0 ? options[0].value : '';
    setSelectedImageRef(defaultImageRef);
    createNewServer.setFieldValue('imageRef', defaultImageRef);

    const selectedOSImage =
      localImageDetails?.find(
        (img) => img?.image_id === defaultImageRef,
      ) ||
      allAvailableImages.find(
        (img) => (img?.image_id || img?.id) === defaultImageRef,
      );
    const defaultUsername =
      image === 'windows' ? 'Administrator' : selectedOSImage?.username || '';
    createNewServer.setFieldValue('username', defaultUsername);
  };

  const createNewKeypair = useFormik({
    initialValues: {
      name: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Keypair Name cannot be empty'),
    }),
    onSubmit: async (values, { resetForm }) => {
      const { name } = values;
      let createKeypairData = { name };
      const { payload } = await dispatch(
        createKeypair({ createKeypairData, projectId: selectedProjectId }),
      );
      if (payload) {
        dispatch(listKeypairs(selectedProjectId));
        downloadPrivateKey(payload.keypair);
        resetForm();
      }
    },
  });

  const downloadPrivateKey = (keypair) => {
    const privateKey = keypair.private_key;
    const filename = `${keypair.name}.pem`;
    const blob = new Blob([privateKey], { type: 'application/x-pem-file' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const handlePaymentDuration = (duration) => {
    if (!duration) return duration;
    return duration.charAt(0).toLowerCase() + duration.slice(1);
  };

  const [selectedImageRef, setSelectedImageRef] = useState('');

  const linuxImages = [
    'fedora',
    'ubuntu',
    'rocky',
    'almalinux',
    'opensuse',
    'debian',
    'centos',
    'oracle',
  ];

  const createNewServer = useFormik({
    initialValues: {
      name: '',
      imagesRef:
        location.pathname === '/client-area/configure-vps' &&
          selectedSnapshot &&
          selectedSnapshot.action === 'launch'
          ? 'snapshot'
          : '',
      imageRef:
        location.pathname === '/client-area/configure-vps' &&
          selectedSnapshot &&
          selectedSnapshot.action === 'launch'
          ? selectedSnapshot?.snapshotId
          : '',
      old_key_pair_name: '',
      username: '',
      user_pass: '',
      confirm_pass: '',
      private_network: '',
      private_subnet: '',
    },
    validationSchema: Yup.lazy(() =>
      Yup.object({
        name: Yup.string().required('Instance Name cannot be empty'),
        imagesRef: Yup.string().required('Please select an image'),
        imageRef: Yup.string().required('Image version cannot be empty'),
        old_key_pair_name:
          (linuxImages.includes(selectedImage) ||
            (location.pathname === '/client-area/configure-vps' &&
              selectedSnapshot?.action === 'launch')) &&
            authMethod === 'keypairs'
            ? Yup.string().required('Key pair cannot be empty')
            : Yup.string().notRequired(),
        username:
          selectedImage === 'windows' ||
            ((linuxImages.includes(selectedImage) ||
              (location.pathname === '/client-area/configure-vps' &&
                selectedSnapshot?.action === 'launch')) &&
              authMethod === 'password')
            ? Yup.string().required('Username is required')
            : Yup.string().notRequired(),
        user_pass:
          selectedImage === 'windows' ||
            ((linuxImages.includes(selectedImage) ||
              (location.pathname === '/client-area/configure-vps' &&
                selectedSnapshot?.action === 'launch')) &&
              authMethod === 'password')
            ? Yup.string()
              .required('Password is required')
              .min(12, 'Password must be at least 12 characters long')
              .matches(
                /[A-Z]/,
                'Password must contain at least one uppercase letter',
              )
              .matches(
                /[a-z]/,
                'Password must contain at least one lowercase letter',
              )
              .matches(/[0-9]/, 'Password must contain at least one digit')
              .matches(
                /[!@#$%^&*(),.?":{}|<>]/,
                'Password must contain at least one special character',
              )
              .test(
                'no-username',
                'Password must not contain any part of the username',
                function (value) {
                  const username = this.parent.username?.trim();
                  return (
                    !username ||
                    !value ||
                    !value.toLowerCase().includes(username.toLowerCase())
                  );
                },
              )
            : Yup.string().notRequired(),
        confirm_pass:
          selectedImage === 'windows' ||
            ((linuxImages.includes(selectedImage) ||
              (location.pathname === '/client-area/configure-vps' &&
                selectedSnapshot?.action === 'launch')) &&
              authMethod === 'password')
            ? Yup.string()
              .required('Confirm Password is required')
              .oneOf([Yup.ref('user_pass'), null], 'Passwords must match')
            : Yup.string().notRequired(),
        private_network:
          networkType === 'private'
            ? Yup.string().required('Please select a private network')
            : Yup.string().notRequired(),
        private_subnet:
          networkType === 'private'
            ? Yup.string().required('Please select a subnet')
            : Yup.string().notRequired(),
      }),
    ),
    onSubmit: async (values, { resetForm, setTouched }) => {
      const {
        name,
        imageRef,
        old_key_pair_name,
        volume_size,
        free_egress,
        username,
        user_pass,
        private_network,
        private_subnet,
      } = values;

      function extractNumber(str) {
        if (!str) return 0;
        const cleanedStr = str.replace(/GB/gi, '').trim();
        return parseInt(cleanedStr, 10) || 0;
      }

      const numericVolumeSize = extractNumber(volume_size);
      const numericEgressSize = extractNumber(free_egress);

      setTouched({
        name: true,
        imagesRef: true,
        imageRef: true,
        old_key_pair_name: true,
        volume_size: true,
        free_egress: true,
        username: true,
        user_pass: true,
        confirm_pass: true,
        private_network: true,
        private_subnet: true,
      });

      const errors = await createNewServer.validateForm();

      const fieldOrder = [
        'imagesRef',
        'imageRef',
        'name',
        'old_key_pair_name',
        'volume_size',
        'free_egress',
        'username',
        'user_pass',
        'private_network',
        'private_subnet',
      ];

      const firstErrorField = fieldOrder.find((field) => errors[field]);

      if (firstErrorField && errorRefs[firstErrorField]?.current) {
        const element = errorRefs[firstErrorField].current;
        if (!element.id) {
          element.id = `error-${firstErrorField}`;
        }
        scroller.scrollTo(element.id, {
          duration: 1000,
          delay: 100,
          smooth: 'easeInOutQuart',
          offset: -100,
          containerId: 'scroll-container',
        });
        return;
      }

      const customFlavorDisc = cartItems[0].diskSize?.replace('GB', '');
      const transformCustomFlavorDisc = parseInt(customFlavorDisc);

      const customFlavorRam = cartItems[0].ramSize?.replace('GB', '');
      const transformCustomFlavorRam = parseInt(customFlavorRam);

      let createServerData;

      if (cartItems[0]?.type === 'Customize') {
        createServerData = {
          name,
          imageRef:
            location.pathname === '/client-area/configure-vps' &&
            selectedSnapshot &&
            selectedSnapshot.action === 'launch'
              ? selectedSnapshot.snapshotId
              : imageRef,

          volume_size: numericVolumeSize || undefined,
          egress_size: numericEgressSize || undefined,

          custom_flavor_ram: transformCustomFlavorRam,
          custom_flavor_disc: transformCustomFlavorDisc,
          custom_flavor_vpcu: cartItems[0].vcpu,

          payment_method: handlePaymentDuration(duration),
          zone_id: selectedZoneId,
        };
      } else {
        createServerData = {
          name,
          flavorRef,
          imageRef:
            location.pathname === '/client-area/configure-vps' &&
              selectedSnapshot &&
              selectedSnapshot.action === 'launch'
              ? selectedSnapshot.snapshotId
              : imageRef,
          flavor_id: flavorId,
          volume_size: numericVolumeSize || undefined,
          egress_size: numericEgressSize || undefined,
          payment_method: handlePaymentDuration(duration),
        };
      }
      if (networkType === 'private') {
      createServerData.network = private_network;
      createServerData.subnet_id = private_subnet;

      delete createServerData.network_type;
    } else {
      createServerData.network_type = 'public';

      delete createServerData.network;
      delete createServerData.subnet_id;
    }

      const currentImageId =
        location.pathname === '/client-area/configure-vps' &&
        selectedSnapshot &&
        selectedSnapshot.action === 'launch'
          ? selectedSnapshot.snapshotId
          : imageRef;

      const filteredImageDetail =
        localImageDetails?.find(
          (imageDetail) => imageDetail?.image_id === currentImageId,
        ) ||
        allAvailableImages.find(
          (imageDetail) =>
            (imageDetail?.image_id || imageDetail?.id) === currentImageId,
        );

      

      if (
        linuxImages.includes(selectedImage) &&
        !(
          location.pathname === '/client-area/configure-vps' &&
          selectedSnapshot &&
          selectedSnapshot.action === 'launch'
        )
      ) {
        if (authMethod === 'keypairs') {
          createServerData.old_key_pair_name = old_key_pair_name;
        } else if (authMethod === 'password') {
          createServerData.username = filteredImageDetail?.username || username;
          createServerData.user_pass = user_pass;
        }
      } else if (selectedImage === 'windows') {
        createServerData.username = filteredImageDetail?.username || username;
        createServerData.user_pass = user_pass;
        createServerData.server_type = 'windows';
      } else if (
        location.pathname === '/client-area/configure-vps' &&
        selectedSnapshot &&
        selectedSnapshot.action === 'launch'
      ) {
        if (authMethod === 'keypairs') {
          createServerData.old_key_pair_name = old_key_pair_name;
        } else if (authMethod === 'password') {
          createServerData.username = filteredImageDetail?.username || username;
          createServerData.user_pass = user_pass;
        }
      }

      const { payload } = await dispatch(
        createServer({ createServerData, projectId: selectedProjectId }),
      );
      const serverId = payload?.server?.id;

      if (
        payload.statuscode === '00' &&
        payload.statusmessage === 'successful'
      ) {
        setLoading(true);

        const pollForActiveStatus = async (
          serverId,
          maxRetries = 30,
          delay = 10000,
        ) => {
          for (let attempt = 0; attempt < maxRetries; attempt++) {
            const { payload } = await dispatch(getResourceStatus({ serverId }));
            if (
              payload?.data?.status === 'active' &&
              payload?.data?.statuscode === '00'
            ) {
              setLoading(false);

              const resourceId = payload?.data?.resource_id;
              const projectId = selectedProjectId;

              const { payload: serverListPayload } = await dispatch(
                listServers(projectId),
              );

              const updatedServerList = serverListPayload
                ?.filter((server) => server.addresses)
                .map((server) => {
                  const externalAddress = server.addresses?.External?.[0]?.addr;
                  const allAddresses = Object.values(server.addresses)
                    .flat()
                    .map((address) => address.addr)
                    .join(', ');
                  return {
                    serverId: server?.id,
                    name: server.name.replace(/_/g, ' '),
                    address: externalAddress || allAddresses || '',
                    status: server.status === 'ACTIVE' ? 'Active' : 'Inactive',
                  };
                });

              const foundServer = updatedServerList?.find(
                (server) => server.serverId === resourceId,
              );

              const currentImageId =
                location.pathname === '/client-area/configure-vps' &&
                selectedSnapshot &&
                selectedSnapshot.action === 'launch'
                  ? selectedSnapshot.snapshotId
                  : imageRef;

              const filteredImageDetail =
                localImageDetails?.find(
                  (imageDetail) => imageDetail?.image_id === currentImageId,
                ) ||
                allAvailableImages.find(
                  (imageDetail) =>
                    (imageDetail?.image_id || imageDetail?.id) === currentImageId,
                );

              

              setIsSuccess(true);

              const loginUsername =
                selectedImage === 'windows' || authMethod === 'password'
                  ? username
                  : location.pathname === '/client-area/configure-vps' &&
                    selectedSnapshot &&
                    selectedSnapshot.action === 'launch'
                    ? getDefaultUsername(selectedSnapshot.snapshotOS)
                    : filteredImageDetail?.username || selectedImage || 'root';

              let ruleToShow;

              if (selectedImage === 'windows' && authMethod === 'password') {
                ruleToShow =
                  networkType === 'private'
                    ? 'windows_private_password'
                    : 'windows_public_password';
              } else if (
                selectedImage !== 'windows' &&
                authMethod === 'password'
              ) {
                ruleToShow =
                  networkType === 'private'
                    ? 'linux_private_password'
                    : 'linux_public_password';
              } else if (authMethod === 'keypairs') {
                ruleToShow =
                  networkType === 'private'
                    ? 'linux_private_keypairs'
                    : 'linux_public_keypairs';
              }

              setSuccessDetails({
                username: loginUsername,
                ipAddress: foundServer?.address || 'IP not yet assigned',
                type: ruleToShow,
                password:
                  authMethod === 'password'
                    ? createNewServer.values.user_pass
                    : null, // Include password only for password auth
              });

              toast.success('Server created successfully', {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: true,
                closeOnClick: false,
                pauseOnHover: false,
                theme: 'colored',
              });

              if (
                location.pathname === '/client-area/configure-vps' &&
                selectedSnapshot &&
                selectedSnapshot.action === 'launch'
              ) {
                sessionStorage.removeItem('selectedServerSnapshot');
                setSelectedSnapshot(null);
              }

              return;
            }
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
          setLoading(false);
        };
        await pollForActiveStatus(serverId);
      }
    },
  });

  const handlePrivateNetworkChange = async (option) => {
    const networkId = option?.target?.value || '';

    setSelectedPrivateNetwork(networkId);
    createNewServer.setFieldValue('private_network', networkId);

    // A subnet belongs to a specific network, so clear any previous
    // subnet selection whenever the network changes.
    createNewServer.setFieldValue('private_subnet', '');
    setPrivateSubnets([]);

    if (!networkId || !selectedProjectId) {
      return;
    }

    try {
      setSubnetLoading(true);

      const response = await apiEndPoints.subnetList(
        selectedProjectId,
        `?network_id=${encodeURIComponent(networkId)}`,
      );

      const responseData =
        response?.data?.data ||
        response?.data ||
        response;

      const subnets =
        responseData?.subnets ||
        responseData?.results ||
        (Array.isArray(responseData) ? responseData : []);

      // Backend should filter by network_id. Keep this defensive filter
      // so only subnets for the selected network can be chosen.
      const networkSubnets = subnets.filter(
        (subnet) =>
          !subnet?.network_id || subnet.network_id === networkId,
      );

      setPrivateSubnets(networkSubnets);
    } catch (error) {
      console.error('Unable to fetch subnets:', error);
      setPrivateSubnets([]);

      toast.error(
        error?.response?.data?.statusmessage ||
          error?.response?.data?.message ||
          error?.message ||
          'Unable to load subnets for the selected network.',
      );
    } finally {
      setSubnetLoading(false);
    }
  };

  const selectedPrivateSubnet = privateSubnets.find(
    (subnet) => subnet.id === createNewServer.values.private_subnet,
  );

  useEffect(() => {
    createNewServer.setFieldValue('imagesRef', selectedImage || '');
  }, [selectedImage]);

  const handleVolumeChange = (e) => {
    setExtraVolumeSize(e.target.value);
    createNewServer.setFieldValue('volume_size', e.target.value);
  };

  const handleEgressChange = (e) => {
    setExtraEgressSize(e.target.value);
    createNewServer.setFieldValue('free_egress', e.target.value);
  };

  const handleCloseModal = () => {
    setIsSuccess(false);
    navigate('/client-area');
    removeFromCart();
    createNewServer.resetForm();
  };

  const handleImageVersionSelection = (option) => {
    if (
      location.pathname === '/client-area/configure-vps' &&
      selectedSnapshot &&
      selectedSnapshot.action === 'launch'
    )
      return;
    const newImageRef = option.target.value;
    setSelectedImageRef(newImageRef);
    createNewServer.setFieldValue('imageRef', newImageRef);

    const selectedOSImage =
      localImageDetails?.find(
        (image) => image?.image_id === newImageRef,
      ) ||
      allAvailableImages.find(
        (image) => (image?.image_id || image?.id) === newImageRef,
      );

    const defaultUsername = selectedOSImage?.username || '';
    createNewServer.setFieldValue('username', defaultUsername);
  };

  useEffect(() => {
  dispatch(getUserVirtualAccount());
}, [dispatch]);

  useEffect(() => {
    const errors = createNewServer.errors;
    const touched = createNewServer.touched;

    const firstErrorField = Object.keys(errors).find(
      (field) => touched[field] && errors[field],
    );

    if (
      selectedImage === null &&
      !(
        location.pathname === '/client-area/configure-vps' &&
        selectedSnapshot &&
        selectedSnapshot.action === 'launch'
      )
    ) {
      errorRefs.imagesRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } else if (firstErrorField && errorRefs[firstErrorField]?.current) {
      errorRefs[firstErrorField].current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [createNewServer.errors, createNewServer.touched, selectedSnapshot]);

  const handleCopyToClipboard = (title, content) => {
    navigator.clipboard.writeText(content).then(() => {
      toast.info(`${title} content has been copied.`, {
        position: 'bottom-right',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: false,
        theme: 'colored',
      });
    });
  };


  const handleProvisionClick = (e) => {
    e.preventDefault();
    if (totalPrice > walletBalance) {
      setShowWalletModal(true);
    } else {
      createNewServer.handleSubmit();
    }
  };

  return (
    <div className="pb-10 min-h-screen">
      <p className="mb-4">Configure your VPS.</p>

      {cartItems.length > 0 ? (
        <form onSubmit={createNewServer.handleSubmit}>
          <div className="grid grid-cols-12 gap-4">
            <div
              id="scroll-container"
              ref={scrollContainerRef}
              className="col-span-12 lg:col-span-8 border dark:border-[#c4c4c424] dark:text-white bg-white dark:bg-slate-800 rounded-md overflow-clip pb-6"
            >
              <div className="px-4 pt-5 grid md:flex justify-between items-center text-lg crimson-font">
                <span>{cartItems[0].name}</span>
                <span className="font-semibold text-[#1da1fc]">
                  {cartItems.length > 0 ? (
                    <>
                      {currency === 'USD' ? 'USD ' : '₦ '}
                      {totalPrice.toLocaleString()}
                    </>
                  ) : (
                    'No price available'
                  )}
                  <span className="text-sm font-normal text-gray-600">
                    {' '}
                    /{duration.toLowerCase()}
                  </span>
                </span>
              </div>

          <div className="p-4 border-b dark:border-b-[#c4c4c424] bg-[#c4c4c410] grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-4 text-xs mt-3">
            <div className="flex items-center gap-2">
              <img src={cpu} alt="" className="h-4" />
              <span>VCPUs: {cartItems[0].vcpu}</span>
            </div>

            <div className="flex items-center gap-2">
              <img src={ram} alt="" className="h-4" />
              <span>RAM: {cartItems[0].ramSize}</span>
            </div>

            <div className="flex items-center gap-2">
              <img src={hdd} alt="" className="h-4" />
              <span>Root disk: {cartItems[0].diskSize}</span>
            </div>

            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4 text-[#1DA1F2]"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>

              <span>1 Public IPv4 IP</span>
            </div>

            <div className="flex items-center gap-2">
              <img src={hdd} alt="" className="h-4" />
              <span>
                Free Egress Bandwidth: {cartItems[0].egressSize}GB
              </span>
            </div>
          </div>

              <div className="border-b dark:border-[#c4c4c424] dark:text-white bg-white dark:bg-slate-800">
                <div className="p-4">
                  <p className="text-sm">1. Select term length</p>
                  <div className="flex items-center mt-2 gap-4">
                    <div
                      onClick={() => handleSelect('Monthly')}
                      className={
                        duration === 'Monthly' ? activeStyle : inactiveStyle
                      }
                    >
                      <span>Monthly</span>
                    </div>
                    <div
                      onClick={() => handleSelect('Yearly')}
                      className={
                        duration === 'Yearly' ? activeStyle : inactiveStyle
                      }
                    >
                      <span className="flex gap-3 items-center">
                        <span>Yearly</span>
                        <span className="text-[10px] bg-black text-white p-1 rounded">
                          2% discount
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-b dark:border-[#c4c4c424] dark:text-white bg-white dark:bg-slate-800">
                <div className="p-4">
                  <div className="w-full" ref={errorRefs.imagesRef}>
                    <p className="text-sm">2. Images</p>
                    {selectedSnapshot &&
                      selectedSnapshot.action === 'launch' ? (
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Selected Snapshot: {selectedSnapshot.snapshotName}
                        </p>
                        <Select
                          name={'imageRef'}
                          options={[
                            {
                              value: selectedSnapshot.snapshotId,
                              label: selectedSnapshot.snapshotName,
                            },
                          ]}
                          value={createNewServer.values.imageRef}
                          disabled={
                            location.pathname ===
                            '/client-area/configure-vps' &&
                            selectedSnapshot &&
                            selectedSnapshot.action === 'launch'
                          }
                          onError={
                            createNewServer.errors.imageRef &&
                              createNewServer.touched.imageRef
                              ? createNewServer.errors.imageRef
                              : null
                          }
                        />
                      </div>
                    ) : (
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div
                          className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'ubuntu'
                            ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                            : ''
                            }`}
                          onClick={() => handleImageClick('ubuntu')}
                        >
                          <div className="flex justify-center mb-4">
                            <img src={ubuntu} alt="Ubuntu" className="h-10" />
                          </div>
                          <p className="text-center">Ubuntu</p>
                        </div>
                        <div
                          className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'debian'
                            ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                            : ''
                            }`}
                          onClick={() => handleImageClick('debian')}
                        >
                          <div className="flex justify-center mb-4">
                            <img src={debian} alt="Debian" className="h-10" />
                          </div>
                          <p className="text-center">Debian</p>
                        </div>
                        <div
                          className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'rocky'
                            ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                            : ''
                            }`}
                          onClick={() => handleImageClick('rocky')}
                        >
                          <div className="flex justify-center mb-4">
                            <img src={rocky} alt="Rocky" className="h-10" />
                          </div>
                          <p className="text-center">Rocky</p>
                        </div>
                        <div
                          className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'almalinux'
                            ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                            : ''
                            }`}
                          onClick={() => handleImageClick('almalinux')}
                        >
                          <div className="flex justify-center mb-4">
                            <SiAlmalinux
                              aria-label="AlmaLinux"
                              className="h-10 w-10"
                            />
                          </div>
                          <p className="text-center">AlmaLinux</p>
                        </div>
                        <div
                          className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'centos'
                            ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                            : ''
                            }`}
                          onClick={() => handleImageClick('centos')}
                        >
                          <div className="flex justify-center mb-4">
                            <img src={centOS} alt="CentOS" className="h-10" />
                          </div>
                          <p className="text-center">CentOS</p>
                        </div>
                        <div
                          className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'oracle'
                            ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                            : ''
                            }`}
                          onClick={() => handleImageClick('oracle')}
                        >
                          <div className="flex justify-center mb-4">
                            <img
                              src={oracle}
                              alt="Oracle Linux"
                              className="h-10"
                            />
                          </div>
                          <p className="text-center">Oracle Linux</p>
                        </div>
                        <div
                          className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'opensuse'
                            ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                            : ''
                            }`}
                          onClick={() => handleImageClick('opensuse')}
                        >
                          <div className="flex justify-center mb-4">
                            <img
                              src={openSUSE}
                              alt="openSUSE"
                              className="h-10"
                            />
                          </div>
                          <p className="text-center">openSUSE</p>
                        </div>
                        <div
                          className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'fedora'
                            ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                            : ''
                            }`}
                          onClick={() => handleImageClick('fedora')}
                        >
                          <div className="flex justify-center mb-4">
                            <img src={fedora} alt="Fedora" className="h-10" />
                          </div>
                          <p className="text-center">Fedora</p>
                        </div>
                        {parseInt(cartItems[0]?.ramSize?.replace(/\D/g, '')) >=
                          4 &&
                          parseInt(
                            cartItems[0]?.diskSize?.replace(/\D/g, ''),
                          ) >= 20 && (
                            <div
                              className={`dark:bg-white/5 p-4 rounded-md border dark:border-[#c4c4c424] cursor-pointer transition-all text-sm ${selectedImage === 'windows'
                                ? 'border bg-primaryBlue/10 border-primaryBlue dark:border-primaryBlue dark:bg-primaryBlue/5'
                                : ''
                                }`}
                              onClick={() => handleImageClick('windows')}
                            >
                              <div className="flex justify-center mb-4">
                                <img
                                  src={windows}
                                  alt="Windows"
                                  className="h-10"
                                />
                              </div>
                              <p className="text-center">Windows</p>
                            </div>
                          )}
                      </div>
                    )}
                    {createNewServer.touched.imagesRef &&
                      createNewServer.errors.imagesRef && (
                        <span className="text-red-500 text-xs">
                          {createNewServer.errors.imagesRef}
                        </span>
                      )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mt-10">
                    {selectedImage && (
                      <div ref={errorRefs.imageRef}>
                        <p className="capitalize">
                          {selectedImage === 'almalinux'
                            ? 'AlmaLinux'
                            : selectedImage}
                        </p>
                        <Select
                          name={'imageRef'}
                          options={[
                            {
                              value: '',
                              label: 'Select Image',
                              disabled: true,
                            },
                            ...getOptionsForSelectedImage(selectedImage),
                          ]}
                          value={createNewServer.values.imageRef}
                          onChange={(option) =>
                            handleImageVersionSelection(option)
                          }
                          disabled={
                            selectedSnapshot &&
                            selectedSnapshot.action === 'launch'
                          }
                          onError={
                            createNewServer.errors.imageRef &&
                              createNewServer.touched.imageRef
                              ? createNewServer.errors.imageRef
                              : null
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-b dark:border-[#c4c4c424] dark:text-white bg-white dark:bg-slate-800">
                <div className="p-4">
                  <p className="text-sm">3. Instance name</p>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2">
                    <div ref={errorRefs.name}>
                      <Input
                        label={'Name'}
                        type={'text'}
                        name={'name'}
                        value={createNewServer.values.name}
                        onBlur={createNewServer.handleBlur}
                        onChange={createNewServer.handleChange}
                        onError={
                          createNewServer.errors.name &&
                            createNewServer.touched.name
                            ? createNewServer.errors.name
                            : null
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-b dark:border-[#c4c4c424] dark:text-white bg-white dark:bg-slate-800">
                <div className="p-4">
                  <p className="text-sm">4. Authentication Method</p>

                  {(linuxImages.includes(selectedImage) ||
                    (location.pathname === '/client-area/configure-vps' &&
                      selectedSnapshot?.action === 'launch')) && (
                      <div className="mt-4">
                        <div className="flex flex-col items-start md:flex-row gap-4 mb-4">
                          <label
                            className={`flex items-start gap-3 p-4 min-h-24 ${authMethod === 'keypairs' && 'border border-primaryBlue'} rounded-lg cursor-pointer w-full md:w-1/2 hover:border-primaryBlue hover:border`}
                          >
                            <input
                              type="radio"
                              name="authMethod"
                              value="keypairs"
                              checked={authMethod === 'keypairs'}
                              onChange={() => setAuthMethod('keypairs')}
                              className="mt-1 w-5 h-5 text-primaryBlue border-gray-300 focus:ring-primaryBlue"
                            />
                            <div className="flex flex-col">
                              <span className="text-base font-medium text-gray-900 dark:text-white">
                                SSH Keys
                              </span>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                A more secure authentication method
                              </span>
                            </div>
                          </label>
                          <label
                            className={`flex items-start gap-3 p-4 min-h-24 ${authMethod === 'password' && 'border border-primaryBlue'} rounded-lg cursor-pointer w-full md:w-1/2 hover:border-primaryBlue hover:border`}
                          >
                            <input
                              type="radio"
                              name="authMethod"
                              value="password"
                              checked={authMethod === 'password'}
                              onChange={() => setAuthMethod('password')}
                              className="mt-1 w-5 h-5 text-primaryBlue border-gray-300 focus:ring-primaryBlue"
                            />
                            <div className="flex flex-col">
                              <span className="text-base font-medium text-gray-900 dark:text-white">
                                Password
                              </span>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                Create a password to access dew (less secure)
                              </span>
                            </div>
                          </label>
                        </div>
                      </div>
                    )}

                  {selectedImage && (
                    <div className="mt-4">
                      {(linuxImages.includes(selectedImage) ||
                        (selectedSnapshot &&
                          selectedSnapshot.action === 'launch')) &&
                        authMethod === 'keypairs' && (
                          <>
                            <div className="flex divide-x text-sm mb-4">
                              <div
                                className={`cursor-pointer pe-4 ${selectedOption === 'existing'
                                  ? 'text-black dark:text-white'
                                  : 'text-[#c4c4c4] dark:text-[#c4c4c450]'
                                  }`}
                                onClick={() => setSelectedOption('existing')}
                              >
                                Select from existing keys
                              </div>
                              <div
                                className={`cursor-pointer ps-4 ${selectedOption === 'new'
                                  ? 'text-black dark:text-white'
                                  : 'text-[#c4c4c4] dark:text-[#c4c4c450]'
                                  }`}
                                onClick={() => setSelectedOption('new')}
                              >
                                Create new key pair
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 items-end">
                              {selectedOption === 'existing' && (
                                <div
                                  className="mt-4"
                                  ref={errorRefs.old_key_pair_name}
                                >
                                  <Select
                                    name={'old_key_pair_name'}
                                    options={[
                                      {
                                        value: '',
                                        label: 'Select Keypair',
                                        disabled: true,
                                      },
                                      ...keypairOptions,
                                    ]}
                                    value={
                                      createNewServer.values.old_key_pair_name
                                    }
                                    onChange={(option) =>
                                      createNewServer.setFieldValue(
                                        'old_key_pair_name',
                                        option.target.value,
                                      )
                                    }
                                    onError={
                                      createNewServer.errors
                                        .old_key_pair_name &&
                                        createNewServer.touched.old_key_pair_name
                                        ? createNewServer.errors
                                          .old_key_pair_name
                                        : null
                                    }
                                  />
                                </div>
                              )}
                              {selectedOption === 'new' && (
                                <div className="grid gap-4">
                                  <div
                                    className="grid gap-4 mt-8"
                                    ref={errorRefs.keypair_name}
                                  >
                                    <Input
                                      label={'Name'}
                                      type={'text'}
                                      name={'name'}
                                      value={createNewKeypair.values.name}
                                      onBlur={createNewKeypair.handleBlur}
                                      onChange={createNewKeypair.handleChange}
                                      onError={
                                        createNewKeypair.errors.name &&
                                          createNewKeypair.touched.name
                                          ? createNewKeypair.errors.name
                                          : null
                                      }
                                    />
                                    <div className="grid">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          createNewKeypair.handleSubmit(e);
                                        }}
                                        className="text-sm md:text-xs opacity-70 text-white bg-[#1DA1F2] rounded px-8 py-4 cursor-pointer"
                                      >
                                        Create Key
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        )}

                      {(selectedImage === 'windows' ||
                        ((linuxImages.includes(selectedImage) ||
                          (selectedSnapshot &&
                            selectedSnapshot.action === 'launch')) &&
                          authMethod === 'password')) && (
                          <div className="mt-4 grid gap-4">
                            <div ref={errorRefs.username}>
                              <Input
                                label={'Username'}
                                type={'text'}
                                name={'username'}
                                value={createNewServer.values.username}
                                disabled
                                onBlur={createNewServer.handleBlur}
                                onChange={createNewServer.handleChange}
                                onError={
                                  createNewServer.errors.username &&
                                    createNewServer.touched.username
                                    ? createNewServer.errors.username
                                    : null
                                }
                              />
                            </div>
                            <div ref={errorRefs.user_pass}>
                              <Input
                                label={'Password'}
                                type={'password'}
                                isPassword={'true'}
                                name={'user_pass'}
                                value={createNewServer.values.user_pass}
                                onBlur={createNewServer.handleBlur}
                                onChange={createNewServer.handleChange}
                                onError={
                                  createNewServer.errors.user_pass &&
                                    createNewServer.touched.user_pass
                                    ? createNewServer.errors.user_pass
                                    : null
                                }
                              />
                              <Input
                                label={'Confirm Password'}
                                type={'password'}
                                isPassword={'true'}
                                name={'confirm_pass'}
                                value={createNewServer.values.confirm_pass}
                                onBlur={createNewServer.handleBlur}
                                onChange={createNewServer.handleChange}
                                onError={
                                  createNewServer.errors.confirm_pass &&
                                    createNewServer.touched.confirm_pass
                                    ? createNewServer.errors.confirm_pass
                                    : null
                                }
                              />
                              <div className="px-6 mt-2 pb-4 text-xs text-black/50 dark:text-white/50">
                                <p className="mb-2">Password must contain:</p>
                                <ul className="list-inside list-disc gap-2 grid">
                                  <li>At least 12 characters</li>
                                  <li>At least one uppercase letter</li>
                                  <li>At least one lowercase letter</li>
                                  <li>At least one digit</li>
                                  <li>At least one special character</li>
                                  <li>
                                    Must not contain any part of the username
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>

              <div className="border-b dark:border-[#c4c4c424] dark:text-white bg-white dark:bg-slate-800">
                <div className="p-4">
                  <p className="text-sm">5. Network Configuration</p>
                  <div className="mt-4">
                    <div className="flex flex-col items-start md:flex-row gap-4 mb-4">
                      <label
                        className={`flex items-start gap-3 p-4 min-h-24 ${networkType === 'public' &&
                          'border border-primaryBlue'
                          } rounded-lg cursor-pointer w-full md:w-1/2 hover:border-primaryBlue hover:border`}
                      >
                        <input
                          type="radio"
                          name="networkType"
                          value="public"
                          checked={networkType === 'public'}
                          onChange={() => {
                            setNetworkType('public');
                            setSelectedPrivateNetwork('');
                            setPrivateSubnets([]);
                            createNewServer.setFieldValue(
                              'private_network',
                              '',
                            );
                            createNewServer.setFieldValue(
                              'private_subnet',
                              '',
                            );
                          }}
                          className="mt-1 w-5 h-5 text-primaryBlue border-gray-300 focus:ring-primaryBlue"
                        />
                        <div className="flex flex-col">
                          <span className="text-base font-medium text-gray-900 dark:text-white">
                            Public Network
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Connect to the external network
                          </span>
                        </div>
                      </label>
                      <label
                        className={`flex items-start gap-3 p-4 min-h-24 ${networkType === 'private' &&
                          'border border-primaryBlue'
                          } rounded-lg cursor-pointer w-full md:w-1/2 hover:border-primaryBlue hover:border`}
                      >
                        <input
                          type="radio"
                          name="networkType"
                          value="private"
                          checked={networkType === 'private'}
                          onChange={() => setNetworkType('private')}
                          className="mt-1 w-5 h-5 text-primaryBlue border-gray-300 focus:ring-primaryBlue"
                        />
                        <div className="flex flex-col">
                          <span className="text-base font-medium text-gray-900 dark:text-white">
                            Private Network
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Select from your private networks
                          </span>
                        </div>
                      </label>
                    </div>
                    {networkType === 'private' && (
                      <div className="mt-4 grid gap-4">
                        <div ref={errorRefs.private_network}>
                          <Select
                            name={'private_network'}
                            options={[
                              {
                                value: '',
                                label: 'Select Private Network',
                                disabled: true,
                              },
                              ...privateNetworkOptions,
                            ]}
                            value={createNewServer.values.private_network}
                            onChange={handlePrivateNetworkChange}
                            onError={
                              createNewServer.errors.private_network &&
                                createNewServer.touched.private_network
                                ? createNewServer.errors.private_network
                                : null
                            }
                          />
                        </div>

                        {createNewServer.values.private_network && (
                          <div ref={errorRefs.private_subnet}>
                            <Select
                              name={'private_subnet'}
                              options={[
                                {
                                  value: '',
                                  label: subnetLoading
                                    ? 'Loading Subnets...'
                                    : privateSubnetOptions.length > 0
                                      ? 'Select Subnet'
                                      : 'No Subnets Available',
                                  disabled: true,
                                },
                                ...privateSubnetOptions,
                              ]}
                              value={createNewServer.values.private_subnet}
                              disabled={subnetLoading}
                              onChange={(option) =>
                                createNewServer.setFieldValue(
                                  'private_subnet',
                                  option.target.value,
                                )
                              }
                              onError={
                                createNewServer.errors.private_subnet &&
                                  createNewServer.touched.private_subnet
                                  ? createNewServer.errors.private_subnet
                                  : null
                              }
                            />

                            {selectedPrivateSubnet?.cidr && (
                              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                CIDR:{' '}
                                <span className="font-medium text-gray-700 dark:text-gray-300">
                                  {selectedPrivateSubnet.cidr}
                                </span>
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="dark:text-white bg-white dark:bg-slate-800">
                <div className="p-4">
                  <p className="text-sm">6. Extra storage (Optional)</p>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2">
                    <div ref={errorRefs.volume_size}>
                      <Input
                        type="text"
                        placeholder={`e.g ${storage}`}
                        name={'volume_size'}
                        value={extraVolumeSize}
                        onBlur={createNewServer.handleBlur}
                        onChange={handleVolumeChange}
                        onError={
                          createNewServer.errors.volume_size &&
                            createNewServer.touched.volume_size
                            ? createNewServer.errors.volume_size
                            : null
                        }
                      />
                      {storage && (
                        <span className="text-gray-500 text-xs mt-2">
                          Extra storage must not exceed {storage}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* <div className="dark:text-white bg-white dark:bg-slate-800">

              {/* <div className="dark:text-white bg-white dark:bg-slate-800">
                <div className="p-4">
                  <p className="text-sm">7. Extra Free Egress Bandwidth (Optional)</p>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2">
                    <div ref={errorRefs.free_egress}>
                      <Input
                        type="text"
                        placeholder={`e.g ${freeEgress}GB`}
                        name={"free_egress"}
                        value={extraEgressSize}
                        onBlur={createNewServer.handleBlur}
                        onChange={handleEgressChange}
                        onError={
                          createNewServer.errors.free_egress && createNewServer.touched.free_egress
                            ? createNewServer.errors.free_egress
                            : null
                        }
                      />
                      {freeEgress && (
                        <span className="text-gray-500 text-xs mt-2">
                          Extra free egress bandwidth must not exceed {freeEgress}GB
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div> */}
            </div>

            <div className="col-span-12 lg:col-span-4 relative">
              <div className="bg-[#1da1f2] rounded text-white md:sticky md:top-24">
                <div className="border-b border-white/30 p-5">
                  <span className="font-bold crimson-font"> Summary </span>
                </div>
                <div className="p-4 text-sm grid gap-4">
                  <div className="grid grid-cols-2 gap-1">
                    <p>Items:</p>
                    <p className="font-semibold">{cartItems.length}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <p>Selected dew:</p>
                    <p className="font-semibold">{cartItems[0].name}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <p>Term length:</p>
                    <p className="font-semibold">{duration}</p>
                  </div>
                  {selectedSnapshot && selectedSnapshot.action === 'launch' && (
                    <div className="grid grid-cols-2 gap-1">
                      <p>Selected Snapshot:</p>
                      <p className="font-semibold">
                        {selectedSnapshot.snapshotName}
                      </p>
                    </div>
                  )}
                  {extraVolumeSize && (
                    <div className="grid grid-cols-2 gap-1">
                      <p>Extra volume:</p>
                      <p className="font-semibold">{extraVolumeSize}</p>
                    </div>
                  )}
                  {extraVolumeSize && (
                    <div className="grid grid-cols-2 gap-1">
                      <p>Extra Volume Cost:</p>
                      <p className="font-semibold">
                        {currency === 'USD' ? 'USD ' : '₦ '}
                        {calculateExtraVolumeCost(
                          extraVolumeSize,
                          currency,
                        ).toLocaleString()}
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-1">
                    <p>Amount:</p>
                    <p className="font-semibold">
                      {currency === 'USD' ? 'USD ' : '₦ '}
                      {totalPrice.toLocaleString()}
                    </p>
                  </div>
                  <div className="mt-4 grid gap-2">
                    <button
                      className={`text-primaryBlue bg-white rounded p-4 text-xs w-full ${createNewServer.isSubmitting || loading
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                        }`}
                      type="submit"
                      onClick={handleProvisionClick}
                      disabled={createNewServer.isSubmitting || loading}
                    >
                      {totalPrice > walletBalance ? 'Pay Now' : 'Provision'}
                    </button>
                    <div
                      onClick={removeFromCart}
                      className={`text-center cursor-pointer text-xs p-4 rounded hover:bg-red-500 transition-all ${createNewServer.isSubmitting || loading ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      style={createNewServer.isSubmitting || loading ? { pointerEvents: "none" } : {}}
                    >
                      Remove
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      ) : (
        <div className="flex justify-center items-center h-[400px]">
          <div className="text-center grid gap-2">
            <p className="text-[#1da1f2] text-xl font-medium crimson-font">
              You haven't selected a VPS to configure
            </p>
            <Link
              to={generatePartnerUrl("/client-area/vps")}
              className="dark:text-white/50 text-black/50 text-sm underline"
            >
              Choose and buy a Flavour
            </Link>
          </div>
        </div>
      )}
      {loading && (
        <div>
          <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loading}
          >
            <CircularProgress color="inherit" />
            <p className="ml-3">Creating server...</p>
          </Backdrop>
        </div>
      )}

      {isSuccess && (
        <div className="fixed w-full bg-black/60 top-0 left-0 z-50 h-screen">
          <div className="flex h-full items-center justify-center p-4">
            <div className="bg-white dark:bg-[#141D26] dark:text-white w-full md:w-[40%] rounded max-h-[90%] overflow-auto">
              <div className="px-6 md:px-10 py-6 border-b dark:border-b-[#c4c4c432] flex justify-between items-center">
                <span className="text-primaryBlue font-medium">
                  {successDetails.type.includes('windows')
                    ? networkType === 'private'
                      ? 'To access your instance'
                      : 'Use Remote Desktop (RDP) to connect'
                    : 'To login to your instance'}
                </span>
                <span onClick={handleCloseModal} className="cursor-pointer">
                  <img src={closeIcon} alt="Close" className="h-5" />
                </span>
              </div>
              <div className="py-10 grid gap-4">
                {/* Private Network - Windows */}
                {successDetails.type === 'windows_private_password' && (
                  <div className="px-6 md:px-10 grid gap-4">
                    <div className="bg-gray-300 p-4 rounded-lg">
                      <h4 className="text-gray-950 font-medium text-sm">
                        To access your instance, you must first attach a
                        floating IP to it.
                      </h4>
                    </div>
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm mb-3">
                          Once a floating IP has been attached:
                        </h4>
                        <h4 className="text-gray-950 font-medium text-sm mb-3">
                          Your Remote Desktop (RDP) credentials to connect with:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          Username: {successDetails.username}
                        </h4>
                        {/* <h4 className="text-gray-950 font-bold text-lg">
                          Password: {successDetails.password}
                        </h4> */}
                        <h4 className="text-gray-950 font-bold text-lg">
                          IP Address:{' '}
                          {successDetails.floatingIp || '<floating-ip>'}
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'RDP Credentials',
                            `Username: ${successDetails.username}\nPassword: ${successDetails.password}\nIP Address: ${successDetails.ipAddress}`,
                          )
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Public Network - Windows */}
                {successDetails.type === 'windows_public_password' && (
                  <div className="px-6 md:px-10 grid gap-4">
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm mb-3">
                          Your Remote Desktop (RDP) credentials to connect with:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          Username: {successDetails.username}
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          IP Address: {successDetails.ipAddress}
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'RDP Credentials',
                            `Username: ${successDetails.username}\nPassword: ${successDetails.password}\nIP Address: ${successDetails.ipAddress}`,
                          )
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Private Network - Linux Password */}
                {successDetails.type === 'linux_private_password' && (
                  <div className="px-6 md:px-10 grid gap-4">
                    <div className="bg-gray-300 p-4 rounded-lg">
                      <h4 className="text-gray-950 font-medium text-sm">
                        To access your instance, you must first attach a
                        floating IP to it.
                      </h4>
                    </div>
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm mb-3">
                          Once a floating IP has been attached, login into your
                          instance by running the command:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          ssh {successDetails.username}@
                          {successDetails.floatingIp || '<floating-ip>'}
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'SSH Credentials',
                            `ssh ${successDetails.username}@${successDetails.floatingIp || '<floating-ip>'}`,
                          )
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Public Network - Linux Password */}
                {successDetails.type === 'linux_public_password' && (
                  <div className="px-6 md:px-10 grid gap-4">
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm mb-3">
                          Login to your instance by running the command:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          ssh {successDetails.username}@
                          {successDetails.ipAddress}
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'SSH Credentials',
                            `ssh ${successDetails.username}@${successDetails.ipAddress}`,
                          )
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Private Network - Linux Keypairs */}
                {successDetails.type === 'linux_private_keypairs' && (
                  <div className="px-6 md:px-10 grid gap-4">
                    <div className="w-full">
                      <h4 className="text-gray-950 font-medium text-sm mb-3">
                        To access your instance, you must first attach a
                        floating IP to it. Once a floating IP has been attached:
                      </h4>
                    </div>
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm">
                          1. Change the permission of your private key:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          chmod 400 /path/to/privatekey.pem
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'Private Key Permissions',
                            'chmod 400 /path/to/privatekey.pem',
                          )
                        }
                      />
                    </div>
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm">
                          2. Login to your instance by running the command:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          ssh -i /path/to/privatekey.pem{' '}
                          {successDetails.username}@
                          {successDetails.floatingIp || '<floating-ip>'}
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'SSH Login',
                            `ssh -i /path/to/privatekey.pem ${successDetails.username}@${successDetails.floatingIp || '<floating-ip>'}`,
                          )
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Public Network - Linux Keypairs */}
                {successDetails.type === 'linux_public_keypairs' && (
                  <div className="px-6 md:px-10 grid gap-4">
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm">
                          1. Change the permission of your private key:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          chmod 400 /path/to/privatekey.pem
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'Private Key Permissions',
                            'chmod 400 /path/to/privatekey.pem',
                          )
                        }
                      />
                    </div>
                    <div className="bg-gray-300 p-4 rounded-lg flex items-center gap-4">
                      <div className="w-full">
                        <h4 className="text-gray-950 font-medium text-sm">
                          2. Login to your instance by running the command:
                        </h4>
                        <h4 className="text-gray-950 font-bold text-lg">
                          ssh -i /path/to/privatekey.pem{' '}
                          {successDetails.username}@{successDetails.ipAddress}
                        </h4>
                      </div>
                      <IoCopyOutline
                        className="text-primaryBlue text-xl cursor-pointer"
                        onClick={() =>
                          handleCopyToClipboard(
                            'SSH Login',
                            `ssh -i /path/to/privatekey.pem ${successDetails.username}@${successDetails.ipAddress}`,
                          )
                        }
                      />
                    </div>
                  </div>
                )}

                <div className="px-6 md:px-10 flex justify-end">
                  <button
                    type="button"
                    className="text-sm rounded text-white bg-[#1DA1F2] px-8 py-4 cursor-pointer"
                    onClick={handleCloseModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

     
    <PaymentModal
      isOpen={showWalletModal}
      onClose={() => setShowWalletModal(false)}
      currency={currency}
      totalPrice={Number(totalPrice || 0)}
      walletBalance={Number(walletBalance || 0)}
      virtualAccount={virtualAccount || {}}
      onPaymentSuccess={async () => {
        // Refresh the wallet after successful payment verification
        await dispatch(getUserVirtualAccount());

        // Close the payment modal
        setShowWalletModal(false);

        // Continue with VPS creation
        createNewServer.handleSubmit();
      }}
    />
    </div>
  );
};

export default ConfigureVPS;
